<template lang="pug">
v-container(fluid)
  v-card
    v-card-title
      | Reporte de Envíos
      v-spacer
      v-btn(color='primary', @click='exportData', :loading='exporting')
        v-icon(left) mdi-download
        |  Exportar
    v-card-text
      // Filtros
      v-row
        v-col(cols="12" sm="6" md="3")
          v-menu(
            ref="dateMenu"
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          )
            template(v-slot:activator="{ on, attrs }")
              v-text-field(
                v-model="dateRangeText"
                label="Rango de Fechas"
                persistent-hint
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                hide-details
              )
            v-date-picker(
              v-model="filters.dateRange"
              range
              no-title
              locale="es-ar"
              first-day-of-week="1"
              @input="dateMenu = false"
            )
        v-col(cols="12" sm="6" md="3")
          v-select(
            v-model="filters.neighborhoods"
            :items="neighborhoods"
            item-text="name"
            item-value="_id"
            label="Barrios"
            prepend-icon="mdi-map-marker"
            multiple
            chips
            small-chips
            deletable-chips
            hide-details
          )
        v-col(cols="12" sm="6" md="3")
          v-select(
            v-model="filters.status"
            :items="statusOptions"
            label="Estado"
            multiple
            chips
            small-chips
            deletable-chips
            hide-details
          )
        v-col(cols="12" sm="6" md="3")
          v-select(
            v-model="filters.paymentMethod"
            :items="paymentOptions"
            label="Método de Pago"
            multiple
            chips
            small-chips
            hide-details
            deletable-chips
          )
      
      // Tarjetas de resumen
      v-row.mt-4
        v-col(cols='12' sm='6' md='3')
          v-card(outlined)
            v-card-text
              .text-overline.mb-1 Total Pedidos
              .text-h4 {{ summary.totalOrders }}
        v-col(cols='12' sm='6' md='3')
          v-card(outlined)
            v-card-text
              .text-overline.mb-1 Tiempo Promedio
              .text-h4 {{ summary.avgDeliveryTime ? Math.round(summary.avgDeliveryTime) : 0 }} min
        v-col(cols='12' sm='6' md='3')
          v-card(outlined)
            v-card-text
              .text-overline.mb-1 Venta Promedio
              .text-h4 ${{ summary.avgOrderAmount ? summary.avgOrderAmount.toFixed(2) : '0.00' }}
        v-col(cols='12' sm='6' md='3')
          v-card(outlined)
            v-card-text
              .text-overline.mb-1 Distancia Promedio
              .text-h4 {{ summary.avgDistance ? summary.avgDistance.toFixed(1) : '0.0' }} km
      
      // Gráficos
      v-row.mt-4
        v-col(cols='12' md='6')
          v-card(outlined)
            v-card-title Distribución por Barrio
            v-card-text.chart-container
              v-skeleton-loader(v-if="loading" type="chart" height="300")
              pie-chart(
                :chart-options="neighborhoodChartOptions"
                :chart-data="neighborhoodChartData"
                :height="300"
                v-else
              )
        v-col(cols='12' md='6')
          v-card(outlined)
            v-card-title Tiempo de Entrega Promedio
            v-card-text.chart-container
              v-skeleton-loader(v-if="loading" type="chart" height="300")
              line-chart(
                :chart-options="deliveryTimeChartOptions"
                :chart-data="deliveryTimeChartData"
                :height="300"
                v-else
              )
      
      v-row.mt-4
        v-col(cols='12' md='6')
          v-card(outlined)
            v-card-title Distribución por Día de la Semana
            v-card-text.chart-container
              v-skeleton-loader(v-if="loading" type="chart" height="300")
              bar-chart(
                :chart-options="dayOfWeekChartOptions"
                :chart-data="dayOfWeekChartData"
                :height="300"
                v-else
              )
        v-col(cols='12' md='6')
          v-card(outlined)
            v-card-title Distribución por Hora
            v-card-text.chart-container
              v-skeleton-loader(v-if="loading" type="chart" height="300")
              bar-chart(
                :chart-options="hourDistributionChartOptions"
                :chart-data="hourDistributionChartData"
                :height="300"
                v-else
              )
      
      // Tabla detallada
      v-card.mt-4(outlined)
        v-card-title Lista de Envíos
        v-card-text
          v-data-table(
            :headers="headers"
            :items="deliveries"
            :loading="loading"
            :options.sync="options"
            :server-items-length="totalDeliveries"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
          )
            template(v-slot:item.status="{ item }")
              v-chip(:color="getStatusColor(item.status)" small)
                span {{ getStatusText(item.status) }}
            template(v-slot:item.deliveryTime="{ item }")
              span {{ item.deliveryTime || 'N/A' }} min
            template(v-slot:item.distance="{ item }")
              span {{ item.distance ? item.distance + ' km' : 'N/A' }}
            template(v-slot:item.amount="{ item }")
              span ${{ item.amount ? item.amount.toFixed(2) : '0.00' }}
            template(v-slot:item.date="{ item }")
              span {{ formatDate(item.date) }}
            template(v-slot:item.actions="{ item }")
              v-btn(icon small @click="viewOrderDetails(item)")
                v-icon mdi-eye
                
  // Diálogo de detalles de orden
  v-dialog(v-model="orderDialog" max-width="800px")
    v-card(v-if="selectedOrder")
      v-card-title 
        | Detalles del Pedido # {{ selectedOrder.number }}
        v-spacer
        v-btn(icon @click="orderDialog = false")
          v-icon mdi-close
      v-card-text
        v-row
          v-col(cols="12" md="6")
            v-list-item
              v-list-item-content
                v-list-item-title Cliente
                v-list-item-subtitle {{ selectedOrder.customerName }}
            v-list-item
              v-list-item-content
                v-list-item-title Fecha
                v-list-item-subtitle {{ formatDate(selectedOrder.date) }}
            v-list-item
              v-list-item-content
                v-list-item-title Estado
                v-list-item-subtitle
                  v-chip(:color="getStatusColor(selectedOrder.status)" small)
                    | {{ getStatusText(selectedOrder.status) }}
            v-list-item
              v-list-item-content
                v-list-item-title Importe
                v-list-item-subtitle ${{ selectedOrder.amount ? selectedOrder.amount.toFixed(2) : '0.00' }}
          v-col(cols="12" md="6")
            v-list-item
              v-list-item-content
                v-list-item-title Dirección
                v-list-item-subtitle {{ selectedOrder.street ? `${selectedOrder.street} ${selectedOrder.number}` : 'Sin dirección' }}{{ selectedOrder.apartment ? `, Dpto: ${selectedOrder.apartment}` : '' }}
            v-list-item
              v-list-item-content
                v-list-item-title Barrio
                v-list-item-subtitle {{ selectedOrder.neighborhood }}
            v-list-item
              v-list-item-content
                v-list-item-title Distancia
                v-list-item-subtitle {{ selectedOrder.distance ? selectedOrder.distance + ' km' : 'N/A' }}
            v-list-item
              v-list-item-content
                v-list-item-title Tiempo de entrega
                v-list-item-subtitle {{ selectedOrder.deliveryTime || 'N/A' }} min
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { Pie, Line, Bar } from 'vue-chartjs'

export default {
  name: 'DeliveryReport',
  
  components: {
    'pie-chart': Pie,
    'line-chart': Line,
    'bar-chart': Bar
  },

  data() {
    return {
      dateMenu: false,
      exporting: false,
      filters: {
        dateRange: [
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().substr(0, 10),
          new Date().toISOString().substr(0, 10)
        ],
        neighborhoods: [],
        status: ['delivered'],
        paymentMethod: []
      },
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['date'],
        sortDesc: [true]
      },
      headers: [
        { text: 'Fecha', value: 'date' },
        { text: 'Nro', value: 'number' },
        { text: 'Cliente', value: 'customerName' },
        { text: 'Barrio', value: 'neighborhood' },
        { text: 'Distancia', value: 'distance' },
        { text: 'Tiempo', value: 'deliveryTime' },
        { text: 'Importe', value: 'amount' },
        { text: 'Estado', value: 'status' },
        { text: 'Acciones', value: 'actions', sortable: false }
      ],
      deliveries: [],
      totalDeliveries: 0,
      statusOptions: [
        { text: 'Entregados', value: 'delivered' },
        { text: 'Cancelados', value: 'cancelled' },
        { text: 'En preparación', value: 'preparing' },
        { text: 'Listos', value: 'ready' }
      ],
      paymentOptions: [
        { text: 'Efectivo', value: 'cash' },
        { text: 'Tarjeta', value: 'card' },
        { text: 'Transferencia', value: 'transfer' }
      ],
      orderDialog: false,
      selectedOrder: null,
      chartColors: ['#B39DDB', '#F48FB1', '#EF9A9A', '#9FA8DA', '#A5D6A7', '#90CAF9', '#E6EE9C', '#FFCC80']
    }
  },

  computed: {
    ...mapState('neighborhoods', {
      neighborhoods: state => state.list,
    }),
    
    ...mapState('deliveryReports', {
      summary: state => state.summary,
      loading: state => state.loading
    }),
    
    dateRangeText() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return ''
      
      const [start, end] = this.filters.dateRange.map(date => {
        const [year, month, day] = date.split('-').map(Number)
        return new Date(year, month - 1, day)
      })
      return `${format(start, 'dd/MM/yyyy', { locale: es })} - ${format(end, 'dd/MM/yyyy', { locale: es })}`
    },
    
    // Configuración para gráficos
    neighborhoodChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom'
        }
      }
    },
    
    neighborhoodChartData() {
      return {
        labels: this.neighborhoodStats.map(item => item.name),
        datasets: [{
          label: 'Pedidos',
          data: this.neighborhoodStats.map(item => item.count),
          backgroundColor: this.chartColors
        }]
      }
    },
    
    deliveryTimeChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            }
          }]
        }
      }
    },
    
    deliveryTimeChartData() {
      return {
        labels: this.timeStats.map(item => item.date),
        datasets: [{
          label: 'Tiempo promedio',
          data: this.timeStats.map(item => item.avgTime),
          backgroundColor: this.chartColors[0],
          borderColor: this.chartColors[0],
          borderWidth: 2,
          fill: true
        }]
      }
    },
    
    dayOfWeekChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            }
          }]
        }
      }
    },
    
    dayOfWeekChartData() {
      return {
        labels: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'],
        datasets: [{
          label: 'Pedidos',
          data: this.dayOfWeekStats,
          backgroundColor: this.chartColors[1],
          borderColor: this.chartColors[1],
          borderWidth: 0
        }]
      }
    },
    
    hourDistributionChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            }
          }]
        }
      }
    },
    
    hourDistributionChartData() {
      return {
        labels: Array.from({length: 24}, (_, i) => `${i}:00`),
        datasets: [{
          label: 'Pedidos',
          data: this.hourlyStats,
          backgroundColor: this.chartColors[2],
          borderColor: this.chartColors[2],
          borderWidth: 0
        }]
      }
    },
    
    // Datos para gráficos
    neighborhoodStats() {
      return this.summary.neighborhoodStats || []
    },
    
    timeStats() {
      return this.summary.timeStats || []
    },
    
    dayOfWeekStats() {
      return this.summary.dayOfWeekStats || [0, 0, 0, 0, 0, 0, 0]
    },
    
    hourlyStats() {
      return this.summary.hourlyStats || Array(24).fill(0)
    }
  },

  watch: {
    'filters.dateRange'(val) {
      if (val && val.length === 2) {
        this.fetchData()
      }
    },
    'filters.neighborhoods'() {
      this.fetchData()
    },
    'filters.status'() {
      this.fetchData()
    },
    'filters.paymentMethod'() {
      this.fetchData()
    },
    options: {
      handler() {
        this.fetchData()
      },
      deep: true
    }
  },

  methods: {
    ...mapActions('deliveryReports', ['fetchDeliveries', 'fetchDeliverySummary', 'exportDeliveries']),
    ...mapActions('neighborhoods', ['fetch']),
    
    formatDate(date) {
      if (!date) return 'N/A'
      return format(new Date(date), 'dd/MM/yyyy', { locale: es })
    },
    
    getStatusText(status) {
      switch (status) {
        case 'delivered': return 'Entregado'
        case 'cancelled': return 'Cancelado'
        case 'preparing': return 'En preparación'
        case 'pending': return 'Pendiente'
        case 'ready': return 'Listo'
        default: return status
      }
    },
    
    getStatusColor(status) {
      switch (status) {
        case 'delivered': return 'success'
        case 'cancelled': return 'error'
        case 'preparing': return 'warning'
        case 'pending': return 'Pendiente'
        case 'ready': return 'info'
        default: return 'grey'
      }
    },
    
    async fetchData() {
      try {
        const params = {
          page: this.options.page,
          limit: this.options.itemsPerPage,
          sort: this.options.sortBy[0] || 'date',
          order: this.options.sortDesc[0] ? 'desc' : 'asc',
          fromDate: this.filters.dateRange[0],
          toDate: this.filters.dateRange[1],
          neighborhoods: this.filters.neighborhoods,
          status: this.filters.status,
          paymentMethods: this.filters.paymentMethod
        }
        
        // Cargar los datos de entregas
        const result = await this.fetchDeliveries(params)
        this.deliveries = result.deliveries
        this.totalDeliveries = result.total
        
        // Cargar datos de resumen para gráficos y métricas
        await this.fetchDeliverySummary(params)
        
      } catch (error) {
        console.error('Error al cargar los datos:', error)
        this.$toast.error('Error al cargar los datos')
      }
    },
    
    async exportData() {
      this.exporting = true
      try {
        // Implementar la funcionalidad de exportación
        const params = {
          fromDate: this.filters.dateRange[0],
          toDate: this.filters.dateRange[1],
          neighborhoods: this.filters.neighborhoods,
          status: this.filters.status,
          paymentMethods: this.filters.paymentMethod
        }
        
        await this.exportDeliveries(params)
        this.$toast.success('Reporte exportado correctamente')
      } catch (error) {
        console.error('Error al exportar los datos:', error)
        this.$toast.error('Error al exportar los datos')
      } finally {
        this.exporting = false
      }
    },
    
    getNeighborhoodName(neighborhood) {
      if (!neighborhood) return 'Sin barrio';
      
      // Nueva estructura con neighborhoodId
      if (neighborhood.neighborhoodId && neighborhood.neighborhoodId.name) {
        return neighborhood.neighborhoodId.name;
      }
      
      // Estructura antigua con name directo
      if (neighborhood.name) {
        return neighborhood.name;
      }
      
      // Si es solo un string (nombre directo)
      if (typeof neighborhood === 'string') {
        return neighborhood;
      }
      
      return 'Sin barrio';
    },

    viewOrderDetails(order) {
      // Crear una copia del pedido para mostrar en el diálogo
      this.selectedOrder = {
        ...order,
        // Asegurar que se muestra el nombre del barrio correctamente
        neighborhood: order.neighborhood || 'Sin barrio',
        // Usar la información de dirección del pedido
        street: order.deliveryInfo?.street || '',
        number: order.deliveryInfo?.number || '',
        apartment: order.deliveryInfo?.apartment || ''
      }
      this.orderDialog = true
      
      // Log para depuración
      console.log('Detalles del pedido:', this.selectedOrder)
    }
  },

  async created() {
    await this.fetch() // Cargar barrios
    this.fetchData()
  }
}
</script>

<style scoped>
.chart-container {
  width: 100%;
}
</style>
